/** @format */

import { styled, keyframes } from "@mui/material/styles";
import { Typography, Box } from "@mui/material";
import { colors } from "../../utility/types/constant";

// ── Animations ────────────────────────────────────────────────────────────────

const floatRandom = keyframes`
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  33%       { transform: translate(22px, -24px) rotate(120deg); }
  66%       { transform: translate(-16px, 16px) rotate(240deg); }
`;

const shimmer = keyframes`
  0%   { background-position: -1000px 0; }
  100% { background-position:  1000px 0; }
`;

const badgePulse = keyframes`
  0%, 100% { transform: scale(1);    box-shadow: 0 0 0  0  rgba(30,193,149,0.65); }
  50%       { transform: scale(1.05); box-shadow: 0 0 0 12px rgba(30,193,149,0); }
`;

const fadeSlideUp = keyframes`
  from { opacity: 0; transform: translateY(32px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const countUp = keyframes`
  from { opacity: 0; transform: scale(0.7); }
  to   { opacity: 1; transform: scale(1); }
`;

const cardGlow = keyframes`
  0%, 100% { box-shadow: 0 8px 32px rgba(30,193,149,0.10); }
  50%       { box-shadow: 0 8px 48px rgba(30,193,149,0.28); }
`;

const penDraw = keyframes`
  from { width: 0; opacity: 0; }
  to   { width: 60px; opacity: 1; }
`;

const starPop = keyframes`
  0%   { transform: scale(0) rotate(-30deg); opacity: 0; }
  60%  { transform: scale(1.3) rotate(5deg); opacity: 1; }
  100% { transform: scale(1) rotate(0deg);   opacity: 1; }
`;

// ── Section Shell ─────────────────────────────────────────────────────────────

export const SectionShell = styled("section")(({ theme }) => ({
  position: "relative",
  padding: "100px 20px 120px",
  overflow: "hidden",
  background: `linear-gradient(160deg, ${colors.primary} 0%, #051442 55%, ${colors.thirdPrimary} 100%)`,
  backdropFilter: "blur(16px)",
  borderTop: "1px solid rgba(255,255,255,0.10)",
  borderBottom: "1px solid rgba(255,255,255,0.08)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05)",

  [theme.breakpoints.down("sm")]: {
    padding: "70px 15px 90px",
  },
}));

export const RadialGlow = styled("div")({
  position: "absolute",
  inset: 0,
  background: `
    radial-gradient(circle at 15% 42%, rgba(30,193,149,0.13) 0%, transparent 52%),
    radial-gradient(circle at 88% 78%, rgba(30,193,149,0.10) 0%, transparent 52%),
    radial-gradient(circle at 55%  8%, rgba(255,255,255,0.05) 0%, transparent 52%)
  `,
  pointerEvents: "none",
  zIndex: 1,
});

export const FloatingOrb = styled(Box)(({ theme }) => ({
  position: "absolute",
  width: "110px",
  height: "110px",
  borderRadius: "50%",
  background: `radial-gradient(circle, rgba(30,193,149,0.20), transparent 70%)`,
  animation: `${floatRandom} 22s ease-in-out infinite`,
  zIndex: 1,
  [theme.breakpoints.down("sm")]: { width: "70px", height: "70px" },
}));

// ── Header Block ──────────────────────────────────────────────────────────────

export const HeaderBlock = styled("div")(({ theme }) => ({
  textAlign: "center",
  marginBottom: "64px",
  position: "relative",
  zIndex: 2,
}));

export const Badge = styled("div")(({ theme }) => ({
  display: "inline-block",
  padding: "10px 28px",
  backgroundColor: colors.mainSecondary,
  color: "#ffffff",
  borderRadius: "50px",
  fontFamily: "'Montserrat', sans-serif",
  fontSize: "0.82rem",
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "1.2px",
  marginBottom: "20px",
  animation: `${badgePulse} 2.2s ease-in-out infinite`,
  boxShadow: "0 4px 18px rgba(30,193,149,0.35)",
  [theme.breakpoints.down("sm")]: { fontSize: "0.72rem", padding: "8px 18px" },
}));

export const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Montserrat', sans-serif",
  fontSize: "clamp(2rem, 4vw, 3.2rem)",
  fontWeight: 900,
  color: "#ffffff",
  marginBottom: "14px",
  lineHeight: 1.15,
  letterSpacing: "-0.02em",
  [theme.breakpoints.down("sm")]: { fontSize: "clamp(1.75rem, 5vw, 2.4rem)" },
}));

export const HighlightText = styled("span")({
  background: `linear-gradient(135deg, ${colors.mainSecondary} 0%, #26e3b5 100%)`,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  display: "inline-block",
  animation: `${shimmer} 3s ease-in-out infinite`,
  backgroundSize: "200% auto",
});

export const SectionSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Montserrat', sans-serif",
  fontSize: "clamp(1.05rem, 2vw, 1.28rem)",
  fontWeight: 400,
  color: "rgba(255,255,255,0.92)",
  maxWidth: "680px",
  margin: "0 auto",
  lineHeight: 1.8,
}));

// ── Stats Strip ───────────────────────────────────────────────────────────────

export const StatsStrip = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: "1px",
  backgroundColor: "rgba(255,255,255,0.08)",
  borderRadius: "20px",
  overflow: "hidden",
  marginBottom: "72px",
  position: "relative",
  zIndex: 2,
  border: "1px solid rgba(255,255,255,0.10)",
  backdropFilter: "blur(10px)",

  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr 1fr",
    gap: "1px",
  },
}));

export const StatCell = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "32px 20px",
  backgroundColor: "rgba(255,255,255,0.04)",
  transition: "background 0.3s ease",
  animation: `${countUp} 0.6s ease-out both`,

  "&:hover": {
    backgroundColor: "rgba(30,193,149,0.10)",
  },
}));

export const StatNumber = styled(Typography)(({ theme }) => ({
  fontFamily: "'Montserrat', sans-serif",
  fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)",
  fontWeight: 900,
  background: `linear-gradient(135deg, ${colors.mainSecondary} 0%, #26e3b5 100%)`,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  lineHeight: 1.1,
  marginBottom: "6px",
}));

export const StatLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "'Montserrat', sans-serif",
  fontSize: "clamp(0.8rem, 1.4vw, 0.95rem)",
  fontWeight: 600,
  color: "rgba(255,255,255,0.82)",
  textAlign: "center",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
}));

// ── Two-Column Layout ─────────────────────────────────────────────────────────

export const ContentGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "40px",
  alignItems: "start",
  position: "relative",
  zIndex: 2,

  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    gap: "48px",
  },
}));

// ── Founder's Note Card ────────────────────────────────────────────────────────

export const FounderCard = styled("div")(({ theme }) => ({
  background: "rgba(255,255,255,0.06)",
  backdropFilter: "blur(18px)",
  borderRadius: "28px",
  padding: "48px 44px",
  border: "1px solid rgba(255,255,255,0.13)",
  position: "relative",
  overflow: "hidden",
  animation: `${cardGlow} 5s ease-in-out infinite`,
  transition: "transform 0.35s cubic-bezier(0.4,0,0.2,1)",

  "&:hover": {
    transform: "translateY(-6px)",
  },

  // Top accent bar
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "3px",
    background: `linear-gradient(90deg, ${colors.mainSecondary}, #26e3b5, ${colors.mainSecondary})`,
    backgroundSize: "200% auto",
    animation: `${shimmer} 3s linear infinite`,
  },

  // Subtle corner orb
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: "-40px",
    right: "-40px",
    width: "180px",
    height: "180px",
    borderRadius: "50%",
    background: `radial-gradient(circle, rgba(30,193,149,0.12), transparent 70%)`,
    pointerEvents: "none",
  },

  [theme.breakpoints.down("sm")]: {
    padding: "32px 24px",
  },
}));

export const QuoteSymbol = styled("div")({
  fontFamily: "Georgia, serif",
  fontSize: "96px",
  lineHeight: "0.6",
  color: colors.mainSecondary,
  opacity: 0.35,
  marginBottom: "12px",
  userSelect: "none",
});

export const NoteLabel = styled(Typography)({
  fontFamily: "'Montserrat', sans-serif",
  fontSize: "0.72rem",
  fontWeight: 800,
  color: colors.mainSecondary,
  textTransform: "uppercase",
  letterSpacing: "2px",
  marginBottom: "20px",
  display: "flex",
  alignItems: "center",
  gap: "8px",

  "&::before": {
    content: '""',
    display: "inline-block",
    width: "24px",
    height: "2px",
    background: colors.mainSecondary,
    borderRadius: "2px",
  },
});

export const NoteText = styled(Typography)(({ theme }) => ({
  fontFamily: "'Montserrat', sans-serif",
  fontSize: "clamp(1rem, 1.7vw, 1.15rem)",
  fontWeight: 400,
  color: "rgba(255,255,255,0.95)",
  lineHeight: 1.85,
  marginBottom: "32px",
  fontStyle: "italic",
}));

export const FounderMeta = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "16px",
  paddingTop: "24px",
  borderTop: "1px solid rgba(255,255,255,0.10)",
});

export const FounderAvatar = styled("div")({
  width: "56px",
  height: "56px",
  borderRadius: "50%",
  background: `linear-gradient(135deg, ${colors.mainSecondary} 0%, #26e3b5 100%)`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "22px",
  fontWeight: 900,
  color: "#fff",
  fontFamily: "'Montserrat', sans-serif",
  flexShrink: 0,
  boxShadow: "0 4px 14px rgba(30,193,149,0.40)",
});

export const FounderInfo = styled("div")({});

export const FounderName = styled(Typography)({
  fontFamily: "'Montserrat', sans-serif",
  fontSize: "1rem",
  fontWeight: 800,
  color: "#ffffff",
  lineHeight: 1.2,
});

export const FounderTitle = styled(Typography)({
  fontFamily: "'Montserrat', sans-serif",
  fontSize: "0.8rem",
  fontWeight: 500,
  color: colors.mainSecondary,
  marginTop: "2px",
});

export const SignatureBar = styled("div")({
  marginTop: "8px",
  height: "2px",
  borderRadius: "2px",
  background: `linear-gradient(90deg, ${colors.mainSecondary}, transparent)`,
  animation: `${penDraw} 1.2s ease-out 0.8s both`,
  width: "60px",
});

// ── Testimonials Column ───────────────────────────────────────────────────────

export const TestimonialsColumn = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "20px",
});

export const TestimonialCard = styled("div")<{ delay?: number }>(
  ({ theme, delay = 0 }) => ({
    background: "rgba(255,255,255,0.05)",
    backdropFilter: "blur(14px)",
    borderRadius: "20px",
    padding: "28px 28px 24px",
    border: "1px solid rgba(255,255,255,0.10)",
    position: "relative",
    overflow: "hidden",
    transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
    animation: `${fadeSlideUp} 0.7s ease-out ${delay}ms both`,

    "&:hover": {
      transform: "translateX(6px)",
      border: "1px solid rgba(30,193,149,0.35)",
      background: "rgba(30,193,149,0.06)",
    },

    // Left accent stripe
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: "20%",
      bottom: "20%",
      width: "3px",
      borderRadius: "0 3px 3px 0",
      background: `linear-gradient(180deg, ${colors.mainSecondary}, transparent)`,
      opacity: 0.7,
    },

    [theme.breakpoints.down("sm")]: {
      padding: "22px 20px 18px",
    },
  })
);

export const StarRow = styled("div")({
  display: "flex",
  gap: "3px",
  marginBottom: "12px",
});

export const StarIcon = styled("span")<{ i: number }>(({ i }) => ({
  color: "#fbbf24",
  fontSize: "16px",
  animation: `${starPop} 0.4s ease-out ${i * 80}ms both`,
  display: "inline-block",
}));

export const TestimonialQuote = styled(Typography)(({ theme }) => ({
  fontFamily: "'Montserrat', sans-serif",
  fontSize: "clamp(0.95rem, 1.6vw, 1.0625rem)",
  fontWeight: 400,
  color: "rgba(255,255,255,0.93)",
  lineHeight: 1.75,
  marginBottom: "18px",
  fontStyle: "italic",
}));

export const TestimonialAuthor = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "12px",
});

export const AuthorAvatar = styled("div")<{ bg: string }>(({ bg }) => ({
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  background: bg,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "14px",
  fontWeight: 700,
  color: "#fff",
  fontFamily: "'Montserrat', sans-serif",
  flexShrink: 0,
}));

export const AuthorInfo = styled("div")({});

export const AuthorName = styled(Typography)({
  fontFamily: "'Montserrat', sans-serif",
  fontSize: "0.875rem",
  fontWeight: 700,
  color: "#ffffff",
  lineHeight: 1.2,
});

export const AuthorRole = styled(Typography)({
  fontFamily: "'Montserrat', sans-serif",
  fontSize: "0.8rem",
  fontWeight: 500,
  color: "rgba(255,255,255,0.72)",
  marginTop: "2px",
});

// ── Bottom CTA Strip ──────────────────────────────────────────────────────────

export const CtaStrip = styled("div")(({ theme }) => ({
  marginTop: "72px",
  position: "relative",
  zIndex: 2,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "20px",
}));

export const CtaTagline = styled(Typography)(({ theme }) => ({
  fontFamily: "'Montserrat', sans-serif",
  fontSize: "clamp(1.1rem, 2vw, 1.3rem)",
  fontWeight: 600,
  color: "rgba(255,255,255,0.82)",
  textAlign: "center",
}));

export const CtaButton = styled("button")(({ theme }) => ({
  fontFamily: "'Montserrat', sans-serif",
  fontSize: "1rem",
  fontWeight: 800,
  color: colors.primary,
  background: `linear-gradient(135deg, ${colors.mainSecondary} 0%, #26e3b5 100%)`,
  border: "none",
  borderRadius: "50px",
  padding: "16px 44px",
  cursor: "pointer",
  letterSpacing: "0.02em",
  transition: "all 0.3s ease",
  boxShadow: "0 6px 24px rgba(30,193,149,0.35)",

  "&:hover": {
    transform: "translateY(-3px) scale(1.03)",
    boxShadow: "0 12px 36px rgba(30,193,149,0.50)",
  },
  "&:active": {
    transform: "translateY(-1px) scale(1.01)",
  },
}));
